import type { Permission, RolePermissions, UserRole } from '@/types'
import { permissionsService } from '@/services/permissionsService'

// Cache para permissões da API
let cachedPermissions: Permission[] | null = null
let permissionsCacheTimestamp: number = 0
const PERMISSIONS_CACHE_DURATION = 10 * 60 * 1000 // 10 minutos

// Definição das permissões disponíveis no sistema
export const PERMISSIONS: Record<string, Permission> = {
  // Dashboard
  DASHBOARD_VIEW: {
    id: 'dashboard_view',
    name: 'Visualizar Dashboard',
    description: 'Permite visualizar o dashboard principal',
    resource: 'dashboard',
    action: 'view'
  },

  // Vistorias
  VISTORIA_VIEW: {
    id: 'vistoria_view',
    name: 'Visualizar Vistorias',
    description: 'Permite visualizar a lista de vistorias',
    resource: 'vistoria',
    action: 'view'
  },
  VISTORIA_CREATE: {
    id: 'vistoria_create',
    name: 'Criar Vistorias',
    description: 'Permite criar novas vistorias',
    resource: 'vistoria',
    action: 'create'
  },
  VISTORIA_EDIT: {
    id: 'vistoria_edit',
    name: 'Editar Vistorias',
    description: 'Permite editar vistorias existentes',
    resource: 'vistoria',
    action: 'edit'
  },
  VISTORIA_DELETE: {
    id: 'vistoria_delete',
    name: 'Excluir Vistorias',
    description: 'Permite excluir vistorias',
    resource: 'vistoria',
    action: 'delete'
  },

  // Calendários
  CALENDARIO_VIEW: {
    id: 'calendario_view',
    name: 'Visualizar Calendários',
    description: 'Permite visualizar calendários acadêmicos',
    resource: 'calendario',
    action: 'view'
  },
  CALENDARIO_CREATE: {
    id: 'calendario_create',
    name: 'Criar Calendários',
    description: 'Permite criar novos calendários',
    resource: 'calendario',
    action: 'create'
  },
  CALENDARIO_EDIT: {
    id: 'calendario_edit',
    name: 'Editar Calendários',
    description: 'Permite editar calendários existentes',
    resource: 'calendario',
    action: 'edit'
  },
  CALENDARIO_DELETE: {
    id: 'calendario_delete',
    name: 'Excluir Calendários',
    description: 'Permite excluir calendários',
    resource: 'calendario',
    action: 'delete'
  },

  // Cardápios
  CARDAPIO_VIEW: {
    id: 'cardapio_view',
    name: 'Visualizar Cardápios',
    description: 'Permite visualizar cardápios',
    resource: 'cardapio',
    action: 'view'
  },
  CARDAPIO_CREATE: {
    id: 'cardapio_create',
    name: 'Criar Cardápios',
    description: 'Permite criar novos cardápios',
    resource: 'cardapio',
    action: 'create'
  },
  CARDAPIO_EDIT: {
    id: 'cardapio_edit',
    name: 'Editar Cardápios',
    description: 'Permite editar cardápios existentes',
    resource: 'cardapio',
    action: 'edit'
  },
  CARDAPIO_DELETE: {
    id: 'cardapio_delete',
    name: 'Excluir Cardápios',
    description: 'Permite excluir cardápios',
    resource: 'cardapio',
    action: 'delete'
  },

  // Geolocalização
  GEOLOCATION_VIEW: {
    id: 'geolocation_view',
    name: 'Visualizar Geolocalização',
    description: 'Permite visualizar dados de geolocalização',
    resource: 'geolocation',
    action: 'view'
  },
  GEOLOCATION_CREATE: {
    id: 'geolocation_create',
    name: 'Criar Geolocalização',
    description: 'Permite criar novos registros de geolocalização',
    resource: 'geolocation',
    action: 'create'
  },
  GEOLOCATION_EDIT: {
    id: 'geolocation_edit',
    name: 'Editar Geolocalização',
    description: 'Permite editar registros de geolocalização',
    resource: 'geolocation',
    action: 'edit'
  },
  GEOLOCATION_DELETE: {
    id: 'geolocation_delete',
    name: 'Excluir Geolocalização',
    description: 'Permite excluir registros de geolocalização',
    resource: 'geolocation',
    action: 'delete'
  },

  // Usuários
  USER_VIEW: {
    id: 'user_view',
    name: 'Visualizar Usuários',
    description: 'Permite visualizar lista de usuários',
    resource: 'user',
    action: 'view'
  },
  USER_CREATE: {
    id: 'user_create',
    name: 'Criar Usuários',
    description: 'Permite criar novos usuários',
    resource: 'user',
    action: 'create'
  },
  USER_EDIT: {
    id: 'user_edit',
    name: 'Editar Usuários',
    description: 'Permite editar usuários existentes',
    resource: 'user',
    action: 'edit'
  },
  USER_DELETE: {
    id: 'user_delete',
    name: 'Excluir Usuários',
    description: 'Permite excluir usuários',
    resource: 'user',
    action: 'delete'
  },

  // Parâmetros
  PARAMETRO_VIEW: {
    id: 'parametro_view',
    name: 'Visualizar Parâmetros',
    description: 'Permite visualizar parâmetros do sistema',
    resource: 'parametro',
    action: 'view'
  },
  PARAMETRO_CREATE: {
    id: 'parametro_create',
    name: 'Criar Parâmetros',
    description: 'Permite criar novos parâmetros',
    resource: 'parametro',
    action: 'create'
  },
  PARAMETRO_EDIT: {
    id: 'parametro_edit',
    name: 'Editar Parâmetros',
    description: 'Permite editar parâmetros existentes',
    resource: 'parametro',
    action: 'edit'
  },
  PARAMETRO_DELETE: {
    id: 'parametro_delete',
    name: 'Excluir Parâmetros',
    description: 'Permite excluir parâmetros',
    resource: 'parametro',
    action: 'delete'
  },

  // Relatórios
  RELATORIO_VIEW: {
    id: 'relatorio_view',
    name: 'Visualizar Relatórios',
    description: 'Permite visualizar relatórios do sistema',
    resource: 'relatorio',
    action: 'view'
  },

  // Configurações
  CONFIG_VIEW: {
    id: 'config_view',
    name: 'Visualizar Configurações',
    description: 'Permite visualizar configurações do sistema',
    resource: 'config',
    action: 'view'
  },
  CONFIG_EDIT: {
    id: 'config_edit',
    name: 'Editar Configurações',
    description: 'Permite editar configurações do sistema',
    resource: 'config',
    action: 'edit'
  },

  // Ajuda
  HELP_VIEW: {
    id: 'help_view',
    name: 'Visualizar Ajuda',
    description: 'Permite acessar a seção de ajuda',
    resource: 'help',
    action: 'view'
  }
}

// Mapeamento de permissões por role
export const ROLE_PERMISSIONS: RolePermissions = {
  administrador: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.VISTORIA_VIEW,
    PERMISSIONS.VISTORIA_CREATE,
    PERMISSIONS.VISTORIA_EDIT,
    PERMISSIONS.VISTORIA_DELETE,
    PERMISSIONS.CALENDARIO_VIEW,
    PERMISSIONS.CALENDARIO_CREATE,
    PERMISSIONS.CALENDARIO_EDIT,
    PERMISSIONS.CALENDARIO_DELETE,
    PERMISSIONS.CARDAPIO_VIEW,
    PERMISSIONS.CARDAPIO_CREATE,
    PERMISSIONS.CARDAPIO_EDIT,
    PERMISSIONS.CARDAPIO_DELETE,
    PERMISSIONS.GEOLOCATION_VIEW,
    PERMISSIONS.GEOLOCATION_CREATE,
    PERMISSIONS.GEOLOCATION_EDIT,
    PERMISSIONS.GEOLOCATION_DELETE,
    PERMISSIONS.USER_VIEW,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_EDIT,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.PARAMETRO_VIEW,
    PERMISSIONS.PARAMETRO_CREATE,
    PERMISSIONS.PARAMETRO_EDIT,
    PERMISSIONS.PARAMETRO_DELETE,
    PERMISSIONS.RELATORIO_VIEW,
    PERMISSIONS.CONFIG_VIEW,
    PERMISSIONS.CONFIG_EDIT,
    PERMISSIONS.HELP_VIEW
  ],
  
  vistoria: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.VISTORIA_VIEW,
    PERMISSIONS.VISTORIA_CREATE,
    PERMISSIONS.VISTORIA_EDIT,
    PERMISSIONS.HELP_VIEW
  ],
  
  calendario: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CALENDARIO_VIEW,
    PERMISSIONS.CALENDARIO_CREATE,
    PERMISSIONS.CALENDARIO_EDIT,
    PERMISSIONS.HELP_VIEW
  ],
  
  cardapio: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CARDAPIO_VIEW,
    PERMISSIONS.CARDAPIO_CREATE,
    PERMISSIONS.CARDAPIO_EDIT,
    PERMISSIONS.HELP_VIEW
  ],
  
  usuario_basico: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.HELP_VIEW
  ]
}

// Mapeamento de rotas para permissões necessárias
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  '/dashboard': ['dashboard_view'],
  '/vistorias': ['vistoria_view'],
  '/vistorias/novo': ['vistoria_create'],
  '/vistorias/editar': ['vistoria_edit'],
  '/vistorias/ver': ['vistoria_view'],
  '/calendarios': ['calendario_view'],
  '/calendarios/novo': ['calendario_create'],
  '/calendarios/editar': ['calendario_edit'],
  '/cardapios': ['cardapio_view'],
  '/cardapios/novo': ['cardapio_create'],
  '/cardapios/editar': ['cardapio_edit'],
  '/geolocations': ['geolocation_view'],
  '/geolocation': ['geolocation_create'],
  '/usuarios': ['user_view'],
  '/usuarios/novo': ['user_create'],
  '/usuarios/editar': ['user_edit'],
  '/parametros': ['parametro_view'],
  '/relatorios': ['relatorio_view'],
  '/configuracoes': ['config_view'],
  '/ajuda': ['help_view']
}

// Cache para usuários da API
let cachedUsers: any[] | null = null
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutos

// Função para obter usuários da API com cache
async function getUsersFromAPI(): Promise<any[]> {
  const now = Date.now()
  
  // Se temos cache válido, usar ele
  if (cachedUsers && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedUsers
  }
  
  try {
    // Importar api dinamicamente para evitar dependência circular
    const { api } = await import('@/services/api')
    const response = await api.get('/get-all-user-local')
    
    // Verificar tanto 'success' quanto 'sucess' (erro de digitação no backend)
    const isSuccess = response.data && (response.data.success || response.data.sucess)
    
    if (isSuccess) {
      // Os dados podem estar em 'data' ou 'user' (dependendo da rota)
      cachedUsers = response.data.data || response.data.user || []
      cacheTimestamp = now
      return cachedUsers || []
    }
  } catch (error) {
    console.warn('Erro ao buscar usuários da API:', error)
  }
  
  // Fallback para localStorage
  try {
    const users = localStorage.getItem('portal_users')
    if (users) {
      cachedUsers = JSON.parse(users)
      cacheTimestamp = now
      return cachedUsers || []
    }
  } catch (error) {
    console.error('Erro ao obter usuários do localStorage:', error)
  }
  
  // Fallback para usuários padrão
  cachedUsers = [
    { login: 'janiocesar', role: 'administrador', isActive: true },
    { login: 'admin', role: 'administrador', isActive: true },
    { login: 'administrador', role: 'administrador', isActive: true }
  ]
  cacheTimestamp = now
  return cachedUsers
}

// Função para obter usuários administradores (síncrona com cache)
export function getAdminUsers(): string[] {
  if (cachedUsers) {
    return cachedUsers
      .filter((user: any) => user.role === 'administrador' && user.isActive)
      .map((user: any) => user.login)
  }
  
  // Fallback síncrono
  try {
    const users = localStorage.getItem('portal_users')
    if (users) {
      const usersData = JSON.parse(users)
      return usersData
        .filter((user: any) => user.role === 'administrador' && user.isActive)
        .map((user: any) => user.login)
    }
  } catch (error) {
    console.error('Erro ao obter usuários administradores:', error)
  }
  
  // Fallback para usuários padrão
  return ['janiocesar', 'admin', 'administrador']
}

// Função para obter mapeamento de usuários para roles (síncrona com cache)
export function getUserRoles(): Record<string, UserRole> {
  if (cachedUsers) {
    const roles: Record<string, UserRole> = {}
    cachedUsers.forEach((user: any) => {
      if (user.isActive) {
        roles[user.login] = user.role
      }
    })
    return roles
  }
  
  // Fallback síncrono
  try {
    const users = localStorage.getItem('portal_users')
    if (users) {
      const usersData = JSON.parse(users)
      const roles: Record<string, UserRole> = {}
      
      usersData.forEach((user: any) => {
        if (user.isActive) {
          roles[user.login] = user.role
        }
      })
      
      return roles
    }
  } catch (error) {
    console.error('Erro ao obter roles de usuários:', error)
  }
  
  // Fallback para roles padrão
  return {
    'maria': 'vistoria',
    'joao': 'administrador',
    'ana': 'usuario_basico'
  }
}

// Função para limpar cache (usar após atualizações)
export function clearUsersCache(): void {
  cachedUsers = null
  cacheTimestamp = 0
}

// Função para inicializar cache (chamar no início da aplicação)
export async function initializeUsersCache(): Promise<void> {
  try {
    await getUsersFromAPI()
  } catch (error) {
    console.warn('Erro ao inicializar cache de usuários:', error)
  }
}

// Função para verificar se um usuário é administrador
export function isAdminUser(login: string): boolean {
  const adminUsers = getAdminUsers()
  return adminUsers.includes(login.toLowerCase())
}

// Função para verificar se um usuário é administrador baseado no campo is_admin
export function isAdminUserByField(user: any): boolean {
  return user && user.is_admin === true
}

// Função para obter role específico de um usuário
export function getUserSpecificRole(login: string): UserRole | null {
  const userRoles = getUserRoles()
  return userRoles[login.toLowerCase()] || null
}

// Função para obter permissões de um ou múltiplos roles
export function getPermissionsByRole(role: UserRole | UserRole[]): Permission[] {
  if (Array.isArray(role)) {
    // Se for array de roles, combinar todas as permissões
    const allPermissions = new Map<string, Permission>()
    
    role.forEach(r => {
      const rolePermissions = ROLE_PERMISSIONS[r] || []
      rolePermissions.forEach(permission => {
        allPermissions.set(permission.id, permission)
      })
    })
    
    return Array.from(allPermissions.values())
  } else {
    // Se for um role único
    return ROLE_PERMISSIONS[role] || []
  }
}

// Função para verificar se um usuário tem uma permissão específica
export function hasPermission(userRole: UserRole | UserRole[] | undefined, permissionId: string): boolean {
  if (!userRole) return false
  
  const rolePermissions = getPermissionsByRole(userRole)
  return rolePermissions.some(permission => permission.id === permissionId)
}

// Função para verificar se um usuário tem permissão para acessar uma rota
export function hasRoutePermission(userRole: UserRole | UserRole[] | undefined, route: string): boolean {
  if (!userRole) return false
  
  const requiredPermissions = ROUTE_PERMISSIONS[route]
  if (!requiredPermissions || requiredPermissions.length === 0) return true
  
  return requiredPermissions.every(permissionId => 
    hasPermission(userRole, permissionId)
  )
}

// Função para obter permissões da API com cache
export async function getPermissionsFromAPI(): Promise<Permission[]> {
  const now = Date.now()

  // Se temos cache válido, usar ele
  if (cachedPermissions && (now - permissionsCacheTimestamp) < PERMISSIONS_CACHE_DURATION) {
    return cachedPermissions
  }

  try {
    const response = await permissionsService.getPermissions()
    
    if (response.success) {
      cachedPermissions = response.data
      permissionsCacheTimestamp = now
      return cachedPermissions
    }
  } catch (error) {
    console.warn('⚠️ Erro ao buscar permissões da API:', error)
  }
  
  // Fallback para permissões padrão
  cachedPermissions = permissionsService.getDefaultPermissions()
  permissionsCacheTimestamp = now
  return cachedPermissions
}

// Função para obter permissões por usuário da API
export async function getUserPermissionsFromAPI(userId: number): Promise<Permission[]> {
  try {
    const response = await permissionsService.getPermissionsByUser(userId)
    
    if (response.success) {
      return response.data
    }
  } catch (error) {
    console.warn('⚠️ Erro ao buscar permissões do usuário:', error)
  }
  
  // Fallback: usar permissões baseadas no role
  return []
}

// Função para limpar cache de permissões
export function clearPermissionsCache(): void {
  cachedPermissions = null
  permissionsCacheTimestamp = 0
}

// Função para inicializar cache de permissões
export async function initializePermissionsCache(): Promise<void> {
  try {
    await getPermissionsFromAPI()
  } catch (error) {
    console.error('❌ Erro ao inicializar cache de permissões:', error)
  }
}

// Função para obter todas as permissões (com fallback)
export function getAllPermissions(): Permission[] {
  if (cachedPermissions) {
    return cachedPermissions
  }
  
  // Fallback para permissões padrão
  return permissionsService.getDefaultPermissions()
}
